// Copyright 2018 Sisense Inc. All rights reserved.

/**
 * @fileoverview Implements the listener storage class. Implementation based on
 * Google Closure Library goog.events.Listener class.
 * @link https://google.github.io/closure-library/api/goog.events.Listener.html
 * @author artem.lit@sisense.com (Artem Lytvynov)
 * @copyright sisense@sisense.com (Sisense Ltd.)
 * @license Sisense License
 */

'use strict';

/**
 * @typedef {import('./eventtarget').EventTarget} EventTarget
 */

/**
 * Counter used to create a unique key
 * @type {number}
 * @private
 */
let listenableKeyCounter_ = 0;

/**
 * Simple class that stores information about a listener
 * @param {!Function} listener Callback function.
 * @param {Function} proxy Wrapper for the listener that patches the event.
 * @param {EventTarget} src Source object for the event.
 * @param {string} type Event type.
 * @param {boolean} capture Whether in capture or bubble phase.
 * @param {Object=} opt_handler Object in whose context to execute the
 *                              callback.
 */
class Listener {
    /**
     * @constructor
     * @param {!Function} listener Callback function.
     * @param {Function} proxy Wrapper for the listener that patches the
     * event.
     * @param {EventTarget} src Source object for the event.
     * @param {string} type Event type.
     * @param {boolean} capture Whether in capture or bubble phase.
     * @param {Object=} opt_handler Object in whose context to execute the
     *                              callback.
     */
    constructor(listener, proxy, src, type, capture, opt_handler) {
        /**
         * If monitoring the Listener instances is enabled,
         * stores the creation stack trace of the Disposable instance.
         * @type {string}
         */
        this.creationStack = '';

        /**
         * Callback function.
         * @type {Function}
         */
        this.listener = listener;

        // noinspection JSUnusedGlobalSymbols
        /**
         * A wrapper over the original listener. This is used solely to
         * handle native browser events (it is used to simulate the capture
         * phase and to patch the event object).
         * @type {Function}
         */
        this.proxy = proxy;

        /**
         * Object or node that callback is listening to
         * @type {EventTarget}
         */
        this.src = src;

        /**
         * The event type.
         * @const {string}
         */
        this.type = type;

        // noinspection PointlessBooleanExpressionJS
        /**
         * Whether the listener is being called in the capture or bubble
         * phase.
         * @const {boolean}
         */
        this.capture = !!capture;

        /**
         * Optional object whose context to execute the listener in
         * @type {Object|undefined}
         */
        this.handler = opt_handler;

        /**
         * The key of the listener.
         * @const {number}
         * @override
         */
        // eslint-disable-next-line
        this.key = ++listenableKeyCounter_;

        /**
         * Whether to remove the listener after it has been called.
         * @type {boolean}
         */
        this.callOnce = false;

        /**
         * Whether the listener has been removed.
         * @type {boolean}
         */
        this.removed = false;

        if (Listener.ENABLE_MONITORING) {
            this.creationStack = new Error().stack;
        }
    }

    /**
     * Marks this listener as removed. This also remove references held by
     * this listener object (such as listener and event source).
     * @returns {void}
     */
    markAsRemoved() {
        this.removed = true;
        this.listener = null;
        // noinspection JSUnusedGlobalSymbols
        this.proxy = null;
        this.src = null;
        this.handler = null;
    }
}

/**
 * Whether to enable the monitoring of the Listener
 * instances. Switching on the monitoring is only recommended for debugging
 * because it has a significant impact on performance and memory usage.
 * @type {boolean}
 */
Listener.ENABLE_MONITORING = false;

// Exports:
exports.Listener = Listener;
